// encrypt.js

const SECRET_KEY = "D729D4C154A7FF55BE2DA7B766FCD51E578A7A1AE7618F93C730740853A0278D"; // 64-char hex = 32 bytes = 256-bit
const AES_KEY_SIZE = 32; // 256-bit
const IV_LENGTH = 12; // 12 bytes for AES-GCM (recommended)
const ENC_ALGO = "AES-GCM";

// Convert hex key to Uint8Array
const hexToBytes = (hex) =>
  new Uint8Array(hex.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));

/**
 * Encrypts a string using AES-GCM
 * @param {string} data - The plain text to encrypt
 * @returns {Promise<string>} - Base64 URL-safe encoded encrypted string
 */
export const encrypt = async (data) => {
  try {
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const encoder = new TextEncoder();
    const encodedData = encoder.encode(data);

    const keyBytes = hexToBytes(SECRET_KEY);
    if (keyBytes.length !== AES_KEY_SIZE) {
      throw new Error("Invalid key length. Must be 32 bytes for AES-256.");
    }

    const key = await crypto.subtle.importKey(
      "raw",
      keyBytes,
      { name: ENC_ALGO },
      false,
      ["encrypt"]
    );

    const encrypted = await crypto.subtle.encrypt(
      { name: ENC_ALGO, iv },
      key,
      encodedData
    );

    // Combine IV + encrypted
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encrypted), iv.length);

    // Base64 URL-safe encoding
    return btoa(String.fromCharCode(...combined))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  } catch (error) {
    console.error("Encryption error:", error);
  }
};

/**
 * Decrypts an AES-GCM encrypted string
 * @param {string} encryptedData - Base64 URL-safe encrypted string
 * @returns {Promise<string>} - Decrypted plain text
 */
export const decrypt = async (encryptedData) => {
  try {
    const base64 = encryptedData
      .replace(/-/g, "+")
      .replace(/_/g, "/");
    const binary = atob(base64);
    const combined = new Uint8Array([...binary].map(char => char.charCodeAt(0)));

    const iv = combined.slice(0, IV_LENGTH);
    const encryptedBytes = combined.slice(IV_LENGTH);

    const keyBytes = hexToBytes(SECRET_KEY);
    if (keyBytes.length !== AES_KEY_SIZE) {
      throw new Error("Invalid key length. Must be 32 bytes for AES-256.");
    }

    const key = await crypto.subtle.importKey(
      "raw",
      keyBytes,
      { name: ENC_ALGO },
      false,
      ["decrypt"]
    );

    const decrypted = await crypto.subtle.decrypt(
      { name: ENC_ALGO, iv },
      key,
      encryptedBytes
    );

    return new TextDecoder().decode(decrypted);
  } catch (error) {
    console.error("Decryption error:", error);
  }
};
